(() => {
  'use strict';

  function viewFor(state, labels) {
    const incomplete = !state.selectionComplete || !state.variantId;
    const pending = state.status === 'pending' || state.status === 'reconciling';
    const unknown = state.status === 'unknown';
    const confirmed = state.status === 'confirmed';
    return {
      incomplete,
      showable: incomplete || state.available || state.locked || confirmed || unknown,
      disabled: Boolean(state.locked || pending || unknown || (!incomplete && !state.available)),
      busy: pending,
      cart: unknown,
      label: pending ? labels.adding : unknown ? labels.cart : confirmed ? labels.added : incomplete ? labels.options : labels.add,
      message: unknown ? labels.unknown : '',
      total: !incomplete && Number.isSafeInteger(state.totalPrice) && state.totalPrice >= 0 ? state.totalPrice : null
    };
  }

  // Release 6.7 (spec 20, 2.1): every width. The main button above the edge, no dialog,
  // no keyboard, no side cart, no cookie banner and no footer in view all still apply.
  function canShow({ mainBottom, mainHeight, viewportTop, blocked, keyboard, showable, footerVisible = false }) {
    return Boolean(showable && mainHeight > 0 && mainBottom <= viewportTop && !blocked && !keyboard && !footerVisible);
  }

  if (typeof document === 'undefined') return;
  if (window.AUSensoryFloatbar) { window.AUSensoryFloatbar.refresh(); return; }
  const selector = '[data-av-sensory-floatbar]';
  const blockers = 'dialog[open], [aria-modal="true"], cart-drawer.is-open, [data-av-mobile-drawer], #shopify-pc__banner, #shopify-privacy__banner-div, [data-av-cookie-banner], [data-av-consent-banner]';
  const controllers = new Map();
  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };
  const setHidden = (node, value) => { if (node && node.hidden !== value) node.hidden = value; };
  const visible = (node) => {
    if (!node.isConnected || node.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
    const box = node.getBoundingClientRect(), style = getComputedStyle(node);
    return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < innerHeight && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0;
  };

  function mount(bar) {
    if (controllers.has(bar) || !window.AUPurchase) return;
    // Release 6.8: kit and creatine bars sit outside product-info and name their root;
    // a paused kit's bar (link mode) has no form and scrolls to the separate adds.
    const linkMode = bar.hasAttribute('data-au-r68-link');
    const form = linkMode ? null : document.getElementById(bar.dataset.formId);
    const root = bar.closest('product-info') || document.getElementById(bar.dataset.rootId || '');
    if ((!form && !linkMode) || !root) return;
    if (form) {
      form.setAttribute('data-au-purchase-form', '');
      window.AUPurchase.register(form);
    }
    const abort = new AbortController(), signal = abort.signal;
    const viewport = window.visualViewport, mobile = matchMedia('(max-width: 767px)');
    const originalSpace = document.documentElement.style.getPropertyValue('--au-purchase-bar-space');
    const originalPriority = document.documentElement.style.getPropertyPriority('--au-purchase-bar-space');
    let frame = 0, viewed = false, disposed = false, stateDirty = false, measuredHeight = 0, screenHeight = innerHeight, screenWidth = innerWidth;
    const labels = Object.fromEntries(['add', 'options', 'adding', 'added', 'unavailable', 'unknown', 'quantity', 'regular'].map(key => [key, bar.dataset[`label${key[0].toUpperCase()}${key.slice(1)}`] || '']));
    labels.cart = bar.querySelector('[data-au-floatbar-cart]')?.textContent.trim() || '';
    const money = (cents) => {
      const formatter = window.theme?.formatMoney || window.Shopify?.formatMoney;
      if (formatter) {
        const text = document.createElement('span');
        text.innerHTML = formatter(cents, bar.dataset.moneyFormat);
        return text.textContent.trim();
      }
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: bar.dataset.currency }).format(cents / 100);
    };
    const blocked = () => Boolean(root.closest('[inert], [aria-hidden="true"]')) || document.documentElement.hasAttribute('data-scroll-locked') || document.body.classList.contains('av49-sensitive-active') || [...document.querySelectorAll(blockers)].some(visible);
    const keyboard = () => {
      if (Math.abs(innerWidth - screenWidth) > 80) { screenWidth = innerWidth; screenHeight = innerHeight; }
      if (!document.activeElement?.matches('input:not([type="hidden"]), textarea, select, [contenteditable="true"]')) { screenHeight = Math.max(screenHeight, innerHeight); return false; }
      return !(viewport?.scale > 1.1) && screenHeight - (viewport?.height || innerHeight) > 140;
    };
    // The bottom edge of the fixed header (Release 6.6): its live height, --au-header-current,
    // under any bar fixed above it, --av-header-sticky-offset.
    const headerEdge = () => {
      const style = getComputedStyle(document.documentElement);
      return (parseFloat(style.getPropertyValue('--au-header-current')) || 0) + (parseFloat(style.getPropertyValue('--av-header-sticky-offset')) || 0);
    };
    const reserve = () => {
      const space = bar.hidden ? '0px' : `${Math.ceil(measuredHeight)}px`;
      if (document.documentElement.style.getPropertyValue('--au-purchase-bar-space') !== space) document.documentElement.style.setProperty('--au-purchase-bar-space', space);
    };
    function syncState() {
      if (disposed || !bar.isConnected || linkMode) return;
      const state = window.AUPurchase.getState(form), button = bar.querySelector('button[form]');
      if (!state || !button) { setHidden(bar, true); reserve(); return; }
      const view = viewFor(state, labels), price = bar.querySelector('[data-au-floatbar-price]');
      const pack = bar.querySelector('[data-au-floatbar-format-source]');
      const cart = bar.querySelector('[data-au-floatbar-cart]');
      setText(bar.querySelector('[data-au-floatbar-format]'), !view.incomplete && pack?.dataset.variantId === String(state.variantId) ? pack.dataset.value : '');
      setText(bar.querySelector('[data-au-floatbar-quantity]'), Number.isSafeInteger(state.quantity) && state.quantity > 1 ? `${labels.quantity} ${state.quantity}` : '');
      const plan = bar.querySelector('[data-au-floatbar-plan]');
      setText(plan, state.sellingPlanId && state.planName ? `${labels.regular}. ${state.planName}` : '');
      setHidden(plan, !state.sellingPlanId || !state.planName);
      setText(price, view.total === null ? '' : money(view.total));
      setHidden(price, view.total === null);
      button.type = view.incomplete ? 'button' : 'submit';
      button.disabled = view.disabled;
      button.setAttribute('aria-busy', String(view.busy));
      button.setAttribute('aria-label', view.label);
      setText(button.querySelector('.au-floatbar__btn-label'), view.label);
      setHidden(cart, !view.cart);
      if (view.cart && document.activeElement === button) cart?.focus({ preventScroll: true });
      setHidden(button, view.cart);
      const status = bar.querySelector('[data-au-floatbar-status]');
      setText(status, view.message);
      setHidden(status, !view.message);
      bar.dataset.purchaseStatus = state.status;
    }
    function syncGeometry() {
      frame = 0;
      if (disposed || !bar.isConnected) return;
      const state = linkMode ? null : window.AUPurchase.getState(form);
      const main = document.getElementById(bar.dataset.mainId || `ProductSubmitButton-${bar.dataset.sectionId}`);
      const view = linkMode ? { showable: true } : state && viewFor(state, labels);
      let show = false;
      // Complete layout reads before changing visibility, content or page reservation.
      if (view?.showable && main) {
        const rect = main.getBoundingClientRect();
        const footer = document.querySelector('footer.footer-sections');
        const footerRect = footer?.getBoundingClientRect();
        const viewportTop = viewport?.offsetTop || 0;
        const footerVisible = Boolean(footerRect && footerRect.height > 0 && footerRect.top < viewportTop + (viewport?.height || innerHeight) && footerRect.bottom > viewportTop);
        // Phones keep the top of the screen; from 768 px the button has to pass the header (spec 2.2).
        const edge = mobile.matches ? viewportTop : viewportTop + headerEdge();
        show = canShow({ mainBottom: rect.bottom, mainHeight: rect.height, viewportTop: edge, blocked: blocked(), keyboard: keyboard(), showable: true, footerVisible });
      }
      if (!sizeObserver && !bar.hidden) measuredHeight = bar.getBoundingClientRect().height;
      if (stateDirty) { stateDirty = false; syncState(); }
      const wasHidden = bar.hidden;
      setHidden(bar, !show);
      reserve();
      // Legacy engines without ResizeObserver measure the newly visible bar on the next frame.
      if (!sizeObserver && wasHidden && show) schedule();
      if (show && !viewed) {
        viewed = true;
        document.dispatchEvent(new CustomEvent('australia:pdp_sticky_viewed', { detail: { product_id: bar.dataset.productId } }));
      }
    }
    const schedule = () => { if (!frame && !disposed) frame = requestAnimationFrame(syncGeometry); };
    const onState = () => { syncState(); schedule(); };
    const onClick = (event) => {
      if (linkMode) {
        const go = event.target.closest('[data-au-r68-bar-link]');
        const target = go && document.getElementById(bar.dataset.auR68Link);
        if (!target) return;
        target.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        target.focus({ preventScroll: true });
        return;
      }
      const button = event.target.closest('button[form]');
      if (!button || !bar.contains(button)) return;
      const state = window.AUPurchase.getState(form);
      if (!state?.selectionComplete || !state.variantId) {
        event.preventDefault();
        const option = root.querySelector('variant-selects input:not([disabled]), variant-selects button:not([disabled]), variant-selects select:not([disabled])');
        option?.scrollIntoView({ block: 'center', behavior: 'instant' });
        option?.focus({ preventScroll: true });
      } else if (!state.locked) form.dataset.avCartSource = 'sticky';
    };
    bar.addEventListener('click', onClick, { signal });
    form?.addEventListener('australia:purchase_state', onState, { signal });
    root.addEventListener('change', schedule, { signal });
    root.addEventListener('input', schedule, { signal });
    for (const type of ['scroll', 'resize', 'pageshow']) window.addEventListener(type, schedule, { passive: true, signal });
    for (const type of ['focusin', 'focusout', 'theme:cart-drawer:open', 'theme:cart-drawer:close', 'toggle']) document.addEventListener(type, schedule, { capture: true, signal });
    const onOverlay = (event) => {
      // Ownership is synchronous, even when a busy frame delays geometry.
      // Closing still recalculates from the current viewport and selection.
      if (event.detail?.open === true) { setHidden(bar, true); reserve(); }
      schedule();
    };
    document.addEventListener('australia:overlay_changed', onOverlay, { capture: true, signal });
    const onOverlayMotionEnd = (event) => {
      if (event.target instanceof Element && (event.target.matches(blockers) || event.target.closest(blockers))) schedule();
    };
    for (const type of ['transitionend', 'animationend']) document.addEventListener(type, onOverlayMotionEnd, { capture: true, signal });
    viewport?.addEventListener('resize', schedule, { passive: true, signal });
    viewport?.addEventListener('scroll', schedule, { passive: true, signal });
    mobile.addEventListener('change', schedule, { signal });
    const innerObserver = new MutationObserver(() => { stateDirty = true; schedule(); });
    innerObserver.observe(bar, { childList: true, subtree: true });
    const stateObserver = new MutationObserver(schedule);
    stateObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-scroll-locked'] });
    stateObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    const blockerObserver = new MutationObserver(records => {
      if (records.some(record => {
        const node = record.target;
        if (record.type === 'attributes' && ['inert', 'aria-hidden'].includes(record.attributeName) && node instanceof Element && node.contains(root)) return true;
        if (node instanceof Element && (node.matches(blockers) || node.closest(blockers))) return true;
        return [...record.addedNodes, ...record.removedNodes].some(child => child instanceof Element && (child.matches(blockers) || child.querySelector(blockers)));
      })) schedule();
    });
    blockerObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'hidden', 'aria-hidden', 'aria-modal', 'inert', 'class', 'style'] });
    const sizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
      const entry = entries.find(item => item.target === bar);
      const size = entry?.borderBoxSize;
      const height = (Array.isArray(size) ? size[0] : size)?.blockSize;
      if (Number.isFinite(height)) measuredHeight = height;
      reserve();
    }) : null;
    sizeObserver?.observe(bar, { box: 'border-box' });
    bar.dataset.ready = 'true';
    const dispose = () => {
      disposed = true;
      abort.abort();
      cancelAnimationFrame(frame);
      innerObserver.disconnect(); stateObserver.disconnect(); blockerObserver.disconnect(); sizeObserver?.disconnect();
      setHidden(bar, true);
      delete bar.dataset.ready;
      if (originalSpace) document.documentElement.style.setProperty('--au-purchase-bar-space', originalSpace, originalPriority);
      else document.documentElement.style.removeProperty('--au-purchase-bar-space');
      controllers.delete(bar);
    };
    controllers.set(bar, dispose);
    syncState();
    schedule();
  }

  function refresh() {
    for (const [bar, dispose] of controllers) if (!bar.isConnected) dispose();
    document.querySelectorAll(selector).forEach(mount);
  }
  document.addEventListener('shopify:section:unload', event => {
    for (const [bar, dispose] of controllers) if (event.target.contains(bar)) dispose();
  });
  for (const event of ['australia:purchase_ready', 'shopify:section:load', 'theme:product-info:loaded']) document.addEventListener(event, refresh);
  window.AUSensoryFloatbar = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();
